import type { HTMLAttributes, ReactNode } from "react";
import { Building2, Landmark, Lock, ShieldAlert, ShieldCheck, Globe2, type LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { useMicrocopy } from "../lib/microcopy";

// ---------------------------------------------------------------- Classification
export type ClassificationLevel = "public" | "internal" | "confidential" | "secret";

const CLASSIFICATION: Record<ClassificationLevel, { bar: string; badge: string; icon: LucideIcon; key: "classificationPublic" | "classificationInternal" | "classificationConfidential" | "classificationSecret" }> = {
  public: { bar: "border-class-public bg-success-50/95 text-success-800", badge: "border-success-100 bg-success-50 text-success-700", icon: Globe2, key: "classificationPublic" },
  internal: { bar: "border-class-internal bg-seablue-50/95 text-seablue-900", badge: "border-seablue-200 bg-seablue-50 text-seablue-900", icon: ShieldCheck, key: "classificationInternal" },
  confidential: { bar: "border-class-confidential bg-warning-50/95 text-warning-800", badge: "border-warning-100 bg-warning-50 text-warning-700", icon: Lock, key: "classificationConfidential" },
  secret: { bar: "border-class-secret bg-danger-50/95 text-danger-800", badge: "border-danger-100 bg-danger-50 text-danger-700", icon: ShieldAlert, key: "classificationSecret" },
};

export interface ClassificationBannerProps {
  level: ClassificationLevel;
  /** `top` sits above the gov bar; `bottom` is fixed to the viewport foot so every printed or captured screen carries the marking. */
  position?: "top" | "bottom";
  /** Overrides the default label (for instance "Confidential - Ministry of Economy only"). */
  label?: ReactNode;
  /** Extra caveat after the level, such as a handling instruction. */
  caveat?: ReactNode;
  className?: string;
}

/**
 * The information-classification strip carried at the top and bottom of every screen showing classified material. The colour follows
 * the level (green public, blue internal, amber confidential, red secret): a tinted strip edged by a rule in the level colour, with
 * text at least 7:1 against the tint.
 */
export function ClassificationBanner({ level, position = "top", label, caveat, className }: ClassificationBannerProps) {
  const m = useMicrocopy();
  const c = CLASSIFICATION[level];
  const Icon = c.icon;
  return (
    <div
      role="note"
      aria-label={`${m("classificationLabel")}: ${m(c.key)}`}
      data-classification={level}
      data-print="show"
      className={cn(
        "flex h-6 w-full items-center justify-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] backdrop-blur-md",
        c.bar,
        position === "bottom" ? "fixed inset-x-0 bottom-0 z-40 border-t" : "border-b",
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      <span>{label ?? m(c.key)}</span>
      {caveat ? <span className="font-normal normal-case tracking-normal opacity-90">· {caveat}</span> : null}
    </div>
  );
}

export function ClassificationBadge({ level, className }: { level: ClassificationLevel; className?: string }) {
  const m = useMicrocopy();
  const c = CLASSIFICATION[level];
  const Icon = c.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider", c.badge, className)} title={m("classificationLabel")}>
      <Icon className="size-3" aria-hidden />
      {m(c.key)}
    </span>
  );
}

// ---------------------------------------------------------------- Tenant badge
export type TenantKind = "ministry" | "oversight" | "platform";

export interface TenantBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Display name in the current language (short name preferred). */
  name: ReactNode;
  kind?: TenantKind;
  /** Tenant accent colour (hex); falls back to the `--tenant-accent` variable. */
  accent?: string | null;
  /** Code shown before the name, e.g. `MOE`. */
  code?: string;
  size?: "sm" | "md";
}

const TENANT_ICON: Record<TenantKind, LucideIcon> = { ministry: Building2, oversight: Landmark, platform: ShieldCheck };

/** A compact chip naming a workspace (ministry, GSOC or platform) with its accent colour as a leading swatch. */
export function TenantBadge({ name, kind = "ministry", accent, code, size = "md", className, style, ...props }: TenantBadgeProps) {
  const Icon = TENANT_ICON[kind];
  return (
    <span
      className={cn(
        "inline-flex min-w-0 items-center gap-1.5 rounded-sm border border-aeblack-100 bg-white font-medium text-strong",
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs",
        className,
      )}
      style={{ ...(accent ? ({ "--badge-accent": accent } as React.CSSProperties) : {}), ...style }}
      {...props}
    >
      <span className="grid size-4 shrink-0 place-items-center rounded-full text-white" style={{ background: "var(--badge-accent, var(--color-tenant))" }} aria-hidden>
        <Icon className="size-2.5" />
      </span>
      {code ? <span className="font-semibold tabular text-subtle" dir="ltr">{code}</span> : null}
      <span className="truncate">{name}</span>
    </span>
  );
}

// ---------------------------------------------------------------- Status pill
export type StatusTone = "neutral" | "info" | "progress" | "success" | "warning" | "danger" | "gold" | "ai" | "muted";

const TONES: Record<StatusTone, { pill: string; dot: string }> = {
  neutral: { pill: "bg-aeblack-100/70 text-aeblack-800", dot: "bg-aeblack-500" },
  muted: { pill: "bg-aeblack-50 text-aeblack-600", dot: "bg-aeblack-300" },
  info: { pill: "bg-techblue-50 text-techblue-700", dot: "bg-techblue-600" },
  progress: { pill: "bg-aegold-50 text-aegold-700", dot: "bg-aegold-500" },
  gold: { pill: "bg-aegold-100 text-aegold-900", dot: "bg-aegold-600" },
  success: { pill: "bg-aegreen-50 text-aegreen-700", dot: "bg-aegreen-600" },
  warning: { pill: "bg-camel-50 text-camel-700", dot: "bg-camel-500" },
  danger: { pill: "bg-aered-50 text-aered-700", dot: "bg-aered-600" },
  ai: { pill: "bg-linear-to-r from-aegold-50 to-aegold-100 text-aegold-900", dot: "bg-aegold-700" },
};

export interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
  icon?: LucideIcon;
  /** Pulsing dot for live states (running, under review). */
  live?: boolean;
  size?: "sm" | "md";
}

/**
 * A status word as a Design System badge (4px corners, semibold, the palette's 50 fill and 700 text) with a leading dot. The colour is
 * never the only signal: the label always carries the meaning.
 */
export function StatusPill({ tone = "neutral", icon: Icon, live, size = "md", className, children, ...props }: StatusPillProps) {
  const t = TONES[tone];
  return (
    <span className={cn("inline-flex items-center gap-1.5 whitespace-nowrap rounded-sm border border-transparent font-semibold", size === "sm" ? "px-1.5 py-0 text-[11px]" : "px-2 py-0.5 text-xs", t.pill, className)} {...props}>
      {Icon ? <Icon className="size-3.5" aria-hidden /> : <span className={cn("size-1.5 rounded-full", t.dot, live && "animate-pulse")} aria-hidden />}
      {children}
    </span>
  );
}
